import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { PerformanceGroupService } from '@/api/generated/services/PerformanceGroupService'
import { PerformanceGroupResponse } from '@/api/generated/models/PerformanceGroupResponse'
import { PerformanceGroupCreateRequest } from '@/api/generated/models/PerformanceGroupCreateRequest'
import { PerformanceGroupUpdateRequest } from '@/api/generated/models/PerformanceGroupUpdateRequest'
import { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'

// Async thunks
export const fetchPerformanceGroups = createAsyncThunk(
  'performanceGroup/fetchPerformanceGroups',
  async (params: {
    workspaceId?: string
    businessId?: string
    q?: string
    page?: number
    perPage?: number
  }) => {
    const response: PaginatedResponse = await PerformanceGroupService.appsPerformanceViewsListPerformanceGroups(params)
    return {
      data: response.data as PerformanceGroupResponse[],
      total: response.meta.total,
      page: response.meta.page,
      perPage: response.meta.per_page,
      totalPages: response.meta.pages
    }
  }
)

export const createPerformanceGroup = createAsyncThunk(
  'performanceGroup/createPerformanceGroup',
  async (data: PerformanceGroupCreateRequest) => {
    const response = await PerformanceGroupService.appsPerformanceViewsCreatePerformanceGroup({
      requestBody: data
    })
    return response
  }
)

export const updatePerformanceGroup = createAsyncThunk(
  'performanceGroup/updatePerformanceGroup',
  async (params: { groupId: string; data: PerformanceGroupUpdateRequest }) => {
    const response = await PerformanceGroupService.appsPerformanceViewsUpdatePerformanceGroup({
      groupId: params.groupId,
      requestBody: params.data
    })
    return response
  }
)

export const deletePerformanceGroup = createAsyncThunk(
  'performanceGroup/deletePerformanceGroup',
  async (groupId: string) => {
    await PerformanceGroupService.appsPerformanceViewsDeletePerformanceGroup({ groupId })
    return groupId
  }
)

export const fetchPerformanceGroup = createAsyncThunk(
  'performanceGroup/fetchPerformanceGroup',
  async (groupId: string) => {
    const response = await PerformanceGroupService.appsPerformanceViewsGetPerformanceGroup({ groupId })
    return response
  }
)

// Initial state
interface PerformanceGroupState {
  groups: PerformanceGroupResponse[]
  selectedGroup: PerformanceGroupResponse | null
  isLoading: boolean
  error: string | null
  pagination: {
    page: number
    perPage: number
    total: number
    totalPages: number
  }
}

const initialState: PerformanceGroupState = {
  groups: [],
  selectedGroup: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    perPage: 100,
    total: 0,
    totalPages: 0
  }
}

// Slice
const performanceGroupSlice = createSlice({
  name: 'performanceGroup',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setSelectedGroup: (state, action: PayloadAction<PerformanceGroupResponse | null>) => {
      state.selectedGroup = action.payload
    },
    clearSelectedGroup: (state) => {
      state.selectedGroup = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch performance groups
      .addCase(fetchPerformanceGroups.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchPerformanceGroups.fulfilled, (state, action) => {
        state.isLoading = false
        state.groups = action.payload.data
        state.pagination = {
          page: action.payload.page,
          perPage: action.payload.perPage,
          total: action.payload.total,
          totalPages: action.payload.totalPages
        }
      })
      .addCase(fetchPerformanceGroups.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '성과 그룹 목록을 불러오는데 실패했습니다.'
      })

      // Create performance group
      .addCase(createPerformanceGroup.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createPerformanceGroup.fulfilled, (state, action) => {
        state.isLoading = false
        state.groups.unshift(action.payload)
      })
      .addCase(createPerformanceGroup.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '성과 그룹 생성에 실패했습니다.'
      })

      // Update performance group
      .addCase(updatePerformanceGroup.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updatePerformanceGroup.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.groups.findIndex(group => group.id === action.payload.id)
        if (index !== -1) {
          state.groups[index] = action.payload
        }
        if (state.selectedGroup?.id === action.payload.id) {
          state.selectedGroup = action.payload
        }
      })
      .addCase(updatePerformanceGroup.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '성과 그룹 수정에 실패했습니다.'
      })

      // Delete performance group
      .addCase(deletePerformanceGroup.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deletePerformanceGroup.fulfilled, (state, action) => {
        state.isLoading = false
        state.groups = state.groups.filter(group => group.id !== action.payload)
        if (state.selectedGroup?.id === action.payload) {
          state.selectedGroup = null
        }
      })
      .addCase(deletePerformanceGroup.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '성과 그룹 삭제에 실패했습니다.'
      })

      // Fetch single performance group
      .addCase(fetchPerformanceGroup.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchPerformanceGroup.fulfilled, (state, action) => {
        state.isLoading = false
        state.selectedGroup = action.payload
      })
      .addCase(fetchPerformanceGroup.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '성과 그룹 조회에 실패했습니다.'
      })
  }
})

export const { clearError, setSelectedGroup, clearSelectedGroup } = performanceGroupSlice.actions
export default performanceGroupSlice.reducer